/**
 * Baby Service
 * Manages baby profiles in Firestore
 */

import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  arrayUnion,
  query,
  where,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { nanoid } from 'nanoid';
import { db } from '@/lib/firebase';
import type { Baby, BabyInput } from '@/types/baby';
import { firestoreToBaby } from '@/types/baby';

/**
 * Create a new baby profile
 */
export async function createBaby(
  ownerId: string,
  babyData: BabyInput
): Promise<Baby> {
  try {
    const babyId = nanoid();
    const babyRef = doc(db, 'babies', babyId);

    const newBaby = {
      name: babyData.name,
      dob: Timestamp.fromDate(babyData.dob),
      gender: babyData.gender,
      photoURL: babyData.photoURL || null,
      ownerId,
      caregivers: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      deletedAt: null,
    };

    await setDoc(babyRef, newBaby);

    // Return the created baby
    return {
      id: babyId,
      ...babyData,
      photoURL: babyData.photoURL || null,
      ownerId,
      caregivers: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      deletedAt: null,
    };
  } catch (error) {
    console.error('Error creating baby:', error);
    throw new Error('Failed to create baby profile');
  }
}

/**
 * Get all babies for a user (owned + shared)
 */
export async function getUserBabies(userId: string): Promise<Baby[]> {
  try {
    const babiesRef = collection(db, 'babies');

    // Simplified queries - single where clause to avoid composite index requirement
    const ownedQuery = query(
      babiesRef,
      where('ownerId', '==', userId)
    );

    const sharedQuery = query(
      babiesRef,
      where('caregivers', 'array-contains', userId)
    );

    const [ownedSnap, sharedSnap] = await Promise.all([
      getDocs(ownedQuery),
      getDocs(sharedQuery),
    ]);

    const babies: Baby[] = [];
    const babyIds = new Set<string>();

    // Add owned babies (filter deletedAt in memory)
    ownedSnap.forEach((doc) => {
      if (!babyIds.has(doc.id)) {
        const baby = firestoreToBaby(doc.id, doc.data());
        // Only include non-deleted babies
        if (!baby.deletedAt) {
          babies.push(baby);
          babyIds.add(doc.id);
        }
      }
    });

    // Add shared babies (avoid duplicates, filter deletedAt in memory)
    sharedSnap.forEach((doc) => {
      if (!babyIds.has(doc.id)) {
        const baby = firestoreToBaby(doc.id, doc.data());
        // Only include non-deleted babies
        if (!baby.deletedAt) {
          babies.push(baby);
          babyIds.add(doc.id);
        }
      }
    });

    // Sort by createdAt descending in memory
    babies.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

    return babies;
  } catch (error) {
    console.error('Error getting user babies:', error);
    return [];
  }
}

/**
 * Get a single baby by ID
 */
export async function getBaby(babyId: string): Promise<Baby | null> {
  try {
    const babyRef = doc(db, 'babies', babyId);
    const babySnap = await getDoc(babyRef);

    if (babySnap.exists()) {
      const baby = firestoreToBaby(babySnap.id, babySnap.data());
      // Don't return deleted babies
      if (baby.deletedAt) {
        return null;
      }
      return baby;
    }

    return null;
  } catch (error) {
    console.error('Error getting baby:', error);
    return null;
  }
}

/**
 * Update baby profile
 */
export async function updateBaby(
  babyId: string,
  updates: Partial<BabyInput>
): Promise<void> {
  try {
    const babyRef = doc(db, 'babies', babyId);

    const updateData: any = {
      ...updates,
      updatedAt: serverTimestamp(),
    };

    // Convert Date to Timestamp if dob is being updated
    if (updates.dob) {
      updateData.dob = Timestamp.fromDate(updates.dob);
    }

    await updateDoc(babyRef, updateData);
  } catch (error) {
    console.error('Error updating baby:', error);
    throw new Error('Failed to update baby profile');
  }
}

/**
 * Soft delete a baby (owner only)
 */
export async function deleteBaby(babyId: string): Promise<void> {
  try {
    const babyRef = doc(db, 'babies', babyId);
    await updateDoc(babyRef, {
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error deleting baby:', error);
    throw new Error('Failed to delete baby profile');
  }
}

/**
 * Add caregiver to baby
 */
export async function addCaregiver(
  babyId: string,
  caregiverId: string
): Promise<void> {
  try {
    const babyRef = doc(db, 'babies', babyId);
    await updateDoc(babyRef, {
      caregivers: arrayUnion(caregiverId),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error adding caregiver:', error);
    throw new Error('Failed to add caregiver');
  }
}

/**
 * Remove caregiver from baby
 */
export async function removeCaregiver(
  babyId: string,
  caregiverId: string
): Promise<void> {
  try {
    const babyRef = doc(db, 'babies', babyId);
    const babySnap = await getDoc(babyRef);

    if (!babySnap.exists()) {
      throw new Error('Baby not found');
    }

    const baby = babySnap.data();
    const caregivers = (baby.caregivers || []).filter(
      (id: string) => id !== caregiverId
    );

    await updateDoc(babyRef, {
      caregivers,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error removing caregiver:', error);
    throw new Error('Failed to remove caregiver');
  }
}

/**
 * Check if user has access to baby
 */
export async function hasAccessToBaby(
  babyId: string,
  userId: string
): Promise<boolean> {
  try {
    const baby = await getBaby(babyId);
    if (!baby) return false;

    return (
      baby.ownerId === userId ||
      baby.caregivers.includes(userId)
    );
  } catch (error) {
    console.error('Error checking baby access:', error);
    return false;
  }
}

/**
 * Check if user is owner of baby
 */
export async function isOwner(
  babyId: string,
  userId: string
): Promise<boolean> {
  try {
    const baby = await getBaby(babyId);
    if (!baby) return false;

    return baby.ownerId === userId;
  } catch (error) {
    console.error('Error checking baby ownership:', error);
    return false;
  }
}

/**
 * Get active baby ID for user
 */
export async function getActiveBabyId(userId: string): Promise<string | null> {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return userSnap.data().activeBabyId || null;
    }

    return null;
  } catch (error) {
    console.error('Error getting active baby ID:', error);
    return null;
  }
}

/**
 * Set active baby ID for user
 */
export async function setActiveBabyId(
  userId: string,
  babyId: string
): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(
      userRef,
      {
        activeBabyId: babyId,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error setting active baby ID:', error);
    throw new Error('Failed to set active baby');
  }
}
